// src/types/flags.ts
// Types and utilities for cell flagging system

export interface CellFlag {
  id: string;
  classId: string;
  lineId: string;
  color: 'pink' | 'orange' | 'teal' | 'indigo' | 'emerald' | 'amber';
  note: string;
  createdAt: Date;
  createdBy: string;
}

// Flag color configurations
// Flag palette mapped onto design tokens (see STYLEGUIDE.md + src/styles/tokens.css).
// The `id` values are stable identifiers persisted with each flag — do NOT rename
// them. Only the class strings are themed, keeping the six options visually
// distinct while staying inside the token set (danger/warn/info/navy/ok/gold).
export const FLAG_COLORS = [
  {
    id: 'pink' as const,
    name: 'Pink',
    bgClass: 'bg-danger-bg',
    textClass: 'text-danger-fg',
    borderClass: 'border-danger-border',
    iconClass: 'text-danger-solid',
    cellBgClass: 'bg-danger-bg',
    cellBorderClass: 'border-danger-border'
  },
  {
    id: 'orange' as const,
    name: 'Orange',
    bgClass: 'bg-warn-bg',
    textClass: 'text-warn-fg',
    borderClass: 'border-warn-border',
    iconClass: 'text-warn-solid',
    cellBgClass: 'bg-warn-bg',
    cellBorderClass: 'border-warn-border'
  },
  {
    id: 'teal' as const,
    name: 'Teal',
    bgClass: 'bg-info-bg',
    textClass: 'text-info-fg',
    borderClass: 'border-info-border',
    iconClass: 'text-info-solid',
    cellBgClass: 'bg-info-bg',
    cellBorderClass: 'border-info-border'
  },
  {
    id: 'indigo' as const,
    name: 'Indigo',
    bgClass: 'bg-navy-100',
    textClass: 'text-navy-800',
    borderClass: 'border-navy-300',
    iconClass: 'text-navy-600',
    cellBgClass: 'bg-navy-50',
    cellBorderClass: 'border-navy-200'
  },
  {
    id: 'emerald' as const,
    name: 'Emerald',
    bgClass: 'bg-ok-bg',
    textClass: 'text-ok-fg',
    borderClass: 'border-ok-border',
    iconClass: 'text-ok-solid',
    cellBgClass: 'bg-ok-bg',
    cellBorderClass: 'border-ok-border'
  },
  {
    id: 'amber' as const,
    name: 'Amber',
    bgClass: 'bg-gold-100',
    textClass: 'text-gold-600',
    borderClass: 'border-gold-300',
    iconClass: 'text-gold-500',
    cellBgClass: 'bg-gold-50',
    cellBorderClass: 'border-gold-300'
  }
] as const;

export type FlagColor = typeof FLAG_COLORS[number]['id'];

// Helper functions
export const getFlagColorConfig = (color: FlagColor) => {
  return FLAG_COLORS.find(c => c.id === color);
};

export const createFlagId = (classId: string, lineId: string) => {
  return `flag_${classId}_${lineId}`;
};

export const isCellFlagged = (flags: CellFlag[], classId: string, lineId: string): CellFlag | null => {
  return flags.find(flag => flag.classId === classId && flag.lineId === lineId) || null;
};

export const getFlagsByColor = (flags: CellFlag[], color: FlagColor): CellFlag[] => {
  return flags.filter(flag => flag.color === color);
};

export const getFlagCount = (flags: CellFlag[]): Record<FlagColor, number> => {
  const counts = {} as Record<FlagColor, number>;
  
  FLAG_COLORS.forEach(color => {
    counts[color.id] = flags.filter(flag => flag.color === color.id).length;
  });
  
  return counts;
};